// src/components/AudioPlayer.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import { PlaylistContext } from '../context/PlaylistContext';
import '../styles/AudioPlayer.css'; // Import the AudioPlayer CSS

// Helper function to extract only the first sentence (up to the first period)
const extractFirstSentence = (fullText = "") => {
    const periodIndex = fullText.indexOf(".");
    if (periodIndex === -1) {
        return fullText.trim();
    }
    return fullText.slice(0, periodIndex + 1).trim();
};

// Helper function to clean a sentence by removing parentheses and their contents, asterisks, etc.
const cleanSentence = (text = "") => {
    if (!text) return "";
    
    // First extract only the first sentence (up to the first period)
    const periodIndex = text.indexOf(".");
    let firstSentence = periodIndex === -1 ? text : text.slice(0, periodIndex + 1);
    
    // Remove parentheses and their contents - handles nested parentheses too
    let cleanedText = firstSentence;
    while (cleanedText.includes('(') && cleanedText.includes(')')) {
        cleanedText = cleanedText.replace(/\([^(]*?\)/g, '');
    }
    
    // Remove asterisks
    cleanedText = cleanedText.replace(/\*/g, '');
    
    // Remove brackets and their contents
    while (cleanedText.includes('[') && cleanedText.includes(']')) {
        cleanedText = cleanedText.replace(/\[[^\[]*?\]/g, '');
    }
    
    // Remove any double spaces that might have been created
    cleanedText = cleanedText.replace(/\s{2,}/g, ' ');
    
    return cleanedText.trim();
};

const AudioPlayer = ({ playlistName, language }) => {
    const { playlists, updatePlaylist } = useContext(PlaylistContext);
    const playlist =
        playlists.find(pl =>
            pl.name?.trim().toLowerCase() === playlistName.trim().toLowerCase() &&
            (pl.language || "default").trim().toLowerCase() === (language || "default").trim().toLowerCase()
        ) || {};
    const tracks = playlist.sentences || [];

    // --- Player Settings State ---
    const [pauseDuration, setPauseDuration] = useState(3); // seconds between tracks
    const [playlistRepeats, setPlaylistRepeats] = useState(1);
    const [playbackSpeeds, setPlaybackSpeeds] = useState(Array(1).fill(1.0));
    const [displayOption, setDisplayOption] = useState('translation');
    const [shuffleEnabled, setShuffleEnabled] = useState(false);
    const [shuffledIndices, setShuffledIndices] = useState([]);

    // --- Playback State ---
    const [currentIndex, setCurrentIndex] = useState(null);
    const [currentPlaylistRepeat, setCurrentPlaylistRepeat] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(1);
    const [progress, setProgress] = useState(0);

    const audioRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const pauseTimeoutRef = useRef(null);

    // State for the custom confirmation modal.
    const [confirmDelete, setConfirmDelete] = useState({ open: false, trackIndex: null });

    // --- Effect: Reset progress when currentIndex changes ---
    useEffect(() => {
        setProgress(0);
    }, [currentIndex]);

    // --- Effect: Ensure playbackSpeeds array matches playlistRepeats ---
    useEffect(() => {
        setPlaybackSpeeds(prev => {
            const newArr = [...prev];
            if (newArr.length < playlistRepeats) {
                return newArr.concat(Array(playlistRepeats - newArr.length).fill(1.0));
            } else if (newArr.length > playlistRepeats) {
                return newArr.slice(0, playlistRepeats);
            }
            return newArr;
        });
    }, [playlistRepeats]);

    // --- Effect: Monitor changes in the track list ---
    useEffect(() => {
        if (!tracks || tracks.length === 0) {
            stopPlayback();
        } else if (currentIndex !== null && currentIndex >= tracks.length) {
            setCurrentIndex(0);
        }
    }, [tracks, currentIndex]);

    // Function to create shuffled indices array
    const shuffleTracks = () => {
        if (!tracks || tracks.length === 0) return [];
        
        const indices = Array.from({ length: tracks.length }, (_, i) => i);
        // Fisher-Yates shuffle algorithm
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        return indices;
    };

    // Generate new shuffled indices when shuffle is enabled or tracks change
    useEffect(() => {
        if (shuffleEnabled && tracks.length > 0) {
            setShuffledIndices(shuffleTracks());
        }
    }, [shuffleEnabled, tracks]);

    // Reshuffle on every repeat
    useEffect(() => {
        if (shuffleEnabled && currentPlaylistRepeat > 0) {
            setShuffledIndices(shuffleTracks());
        }
    }, [currentPlaylistRepeat, shuffleEnabled]);

    // Toggle shuffle function
    const toggleShuffle = () => {
        setShuffleEnabled(prev => !prev);
    };

    // Get the actual track index based on shuffle state
    const getActualTrackIndex = (displayedIndex) => {
        if (!shuffleEnabled || shuffledIndices.length === 0) {
            return displayedIndex;
        }
        return shuffledIndices[displayedIndex];
    };

    // --- Main Effect: Audio Playback ---
    useEffect(() => {
        if (currentIndex === null) return;
        const audio = audioRef.current;
        if (!audio || tracks.length === 0) return;

        clearInterval(progressIntervalRef.current);
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);

        // Use the actual track index based on shuffle state
        const actualIndex = getActualTrackIndex(currentIndex);
        audio.src = tracks[actualIndex].audioSrc;
        audio.volume = volume;
        audio.currentTime = 0;

        console.log(`Loading audio track: ${audio.src}, Language: ${language}`);

        const handleError = (e) => {
            console.error(`Audio error for ${language} track:`, e);
            if (language && language.toLowerCase() === 'korean') {
                console.error("Korean audio playback error. Details:", {
                    audio: audio.src,
                    error: e.message || "Unknown error",
                    trackData: tracks[actualIndex]
                });
            }
        };

        const handleCanPlayThrough = () => {
            // Use a lower threshold for Korean language (1 second) since Korean audio files are typically shorter
            const minDuration = language && language.toLowerCase() === 'korean' ? 1 : 2;
            
            if (audio.duration < minDuration) {
                console.warn(`Audio duration too short (${audio.duration}s), but will play anyway.`);
            }
            
            audio.playbackRate = playbackSpeeds[currentPlaylistRepeat] || 1.0;
            audio.play()
                .then(() => {
                    setIsPlaying(true);
                    progressIntervalRef.current = setInterval(() => {
                        if (audio.duration) {
                            setProgress((audio.currentTime / audio.duration) * 100);
                        }
                    }, 500);
                })
                .catch(err => {
                    console.error(`Error playing audio (${language}):`, err);
                    // Try again with a slight delay for Korean tracks
                    if (language && language.toLowerCase() === 'korean') {
                        setTimeout(() => {
                            audio.play().catch(e => console.error("Retry failed:", e));
                        }, 1000);
                    }
                });
        };

        const handleEnded = () => {
            clearInterval(progressIntervalRef.current);
            if (pauseTimeoutRef.current) {
                clearTimeout(pauseTimeoutRef.current);
                pauseTimeoutRef.current = null;
            }
            setCurrentIndex(prevIndex => {
                if (prevIndex === tracks.length - 1 && currentPlaylistRepeat === playlistRepeats - 1) {
                    setIsPlaying(false);
                    return null;
                }
                pauseTimeoutRef.current = setTimeout(() => {
                    setCurrentIndex(innerIndex => {
                        if (innerIndex < tracks.length - 1) {
                            return innerIndex + 1;
                        } else if (innerIndex === tracks.length - 1 && currentPlaylistRepeat < playlistRepeats - 1) {
                            setCurrentPlaylistRepeat(prevRepeat => prevRepeat + 1);
                            return 0;
                        }
                        return innerIndex;
                    });
                    setIsPlaying(true);
                }, pauseDuration * 1000);
                return prevIndex;
            });
        };

        audio.addEventListener('canplaythrough', handleCanPlayThrough);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('error', handleError);

        const fallbackTimeout = setTimeout(() => {
            if (!isPlaying && audio.readyState >= 3) {
                audio.play()
                    .then(() => {
                        setIsPlaying(true);
                        progressIntervalRef.current = setInterval(() => {
                            if (audio.duration) {
                                setProgress((audio.currentTime / audio.duration) * 100);
                            }
                        }, 500);
                    })
                    .catch(err => console.error("Fallback error playing audio:", err));
            }
        }, 5000);

        return () => {
            clearTimeout(fallbackTimeout);
            audio.removeEventListener('canplaythrough', handleCanPlayThrough);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
            clearInterval(progressIntervalRef.current);
            if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
        };
    }, [currentIndex, currentPlaylistRepeat, tracks, volume, pauseDuration, playlistRepeats, playbackSpeeds, isPlaying, shuffleEnabled, shuffledIndices, language]);

    const togglePlayPause = () => {
        if (!tracks || tracks.length === 0) return;
        const audio = audioRef.current;
        if (!audio) return;
        if (currentIndex === null) {
            setCurrentIndex(0);
            return;
        }
        if (isPlaying) {
            audio.pause();
            clearInterval(progressIntervalRef.current);
            setIsPlaying(false);
        } else {
            audio.play()
                .then(() => {
                    progressIntervalRef.current = setInterval(() => {
                        if (audio.duration) {
                            setProgress((audio.currentTime / audio.duration) * 100);
                        }
                    }, 500);
                    setIsPlaying(true);
                })
                .catch(err => console.error("Error resuming audio:", err));
        }
    };

    const stopPlayback = () => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.currentTime = 0;
        }
        clearInterval(progressIntervalRef.current);
        if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
        setIsPlaying(false);
        setProgress(0);
        setCurrentPlaylistRepeat(0);
        setCurrentIndex(null);
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (audioRef.current) {
            audioRef.current.volume = newVolume;
        }
    };

    const handleTrackClick = (index) => {
        if (shuffleEnabled) {
            // In shuffle mode, we need to find where this track is in the shuffled list
            const shuffledPosition = shuffledIndices.indexOf(index);
            if (shuffledPosition !== -1) {
                setCurrentIndex(shuffledPosition);
            } else {
                // If not found (shouldn't happen), start from beginning
                setCurrentIndex(0);
            }
        } else {
            // Regular mode - direct index
            setCurrentIndex(index);
        }
        setIsPlaying(false);
    };

    // Helpers for display:
    // getTrackTitle is used for the track list.
    const getTrackTitle = (track) =>
        displayOption === 'translation'
            ? cleanSentence(track.translation) || cleanSentence(track.targetSentence)
            : cleanSentence(track.targetSentence);

    // Get the displayed track for the currently playing track
    const nowPlayingTrack = 
        currentIndex !== null && tracks[getActualTrackIndex(currentIndex)]
            ? tracks[getActualTrackIndex(currentIndex)]
            : null;

    // The header title is the opposite of the track list:
    // If "translation" is selected, show the target language in the header;
    // if "target" is selected, show the translation (or fallback to target sentence).
    const nowPlayingTitle =
        nowPlayingTrack
            ? displayOption === 'translation'
                ? cleanSentence(nowPlayingTrack.targetSentence)
                : cleanSentence(nowPlayingTrack.translation) || cleanSentence(nowPlayingTrack.targetSentence)
            : '';

    const speedOptions = Array.from({ length: 6 }, (_, i) =>
        Number((0.5 + i * 0.1).toFixed(1))
    );

    // Delete Handlers
    const initiateDelete = (index) => {
        setConfirmDelete({ open: true, trackIndex: index });
    };

    const confirmDeleteTrack = () => {
        const index = confirmDelete.trackIndex;
        const newTracks = [...tracks];
        newTracks.splice(index, 1);
        // Update using both playlistName and language.
        // Find the playlist ID and update with the correct parameters
        if (playlist && playlist._id) {
            updatePlaylist(playlist._id, { sentences: newTracks });
        }
        if (currentIndex !== null) {
            if (index < currentIndex) {
                setCurrentIndex(currentIndex - 1);
            } else if (index === currentIndex) {
                setCurrentIndex(newTracks.length > 0 ? (index >= newTracks.length ? newTracks.length - 1 : index) : null);
            }
        }
        setConfirmDelete({ open: false, trackIndex: null });
    };

    return (
        <div className="audioPlayerContainer">
            <h2 className="audioPlayerPlaylistName">{playlistName}</h2>

            {/* Audio Player Settings */}
            <div className="audioPlayerSettingsPanel">
                <h3 className="audioPlayerSettingsTitle">Audio Player Settings</h3>
                <div className="audioPlayerSettingRow">
                    <label className="audioPlayerSettingLabel">
                        Pause duration between tracks (s):
                    </label>
                    <select
                        className="audioPlayerSelect"
                        value={pauseDuration}
                        onChange={(e) => setPauseDuration(Number(e.target.value))}
                    >
                        {Array.from({ length: 10 }, (_, i) => i + 1).map(num => (
                            <option key={num} value={num}>{num}</option>
                        ))}
                    </select>
                </div>
                <div className="audioPlayerSettingRow">
                    <label className="audioPlayerSettingLabel">
                        Playlist # of Repeats:
                    </label>
                    <select
                        className="audioPlayerSelect"
                        value={playlistRepeats}
                        onChange={(e) => setPlaylistRepeats(Number(e.target.value))}
                    >
                        {Array.from({ length: 4 }, (_, i) => i + 1).map(num => (
                            <option key={num} value={num}>{num}</option>
                        ))}
                    </select>
                </div>
                {Array.from({ length: playlistRepeats }, (_, i) => (
                    <div key={i} className="audioPlayerSettingRow">
                        <span className="audioPlayerSettingLabel">Repeat #{i + 1} Speed:</span>
                        <select
                            className="audioPlayerSelect"
                            value={playbackSpeeds[i]}
                            onChange={(e) => {
                                const newSpeeds = [...playbackSpeeds];
                                newSpeeds[i] = Number(e.target.value);
                                setPlaybackSpeeds(newSpeeds);
                            }}
                        >
                            {speedOptions.map(speed => (
                                <option key={speed} value={speed}>{speed}x</option>
                            ))}
                        </select>
                    </div>
                ))}
                <div className="audioPlayerSettingRow">
                    <label className="audioPlayerSettingLabel">Display Track Title:</label>
                    <select
                        className="audioPlayerSelect"
                        value={displayOption}
                        onChange={(e) => setDisplayOption(e.target.value)}
                    >
                        <option value="translation">Translation</option>
                        <option value="target">Target Language</option>
                    </select>
                </div>
            </div>

            {/* Playback Controls */}
            {currentIndex !== null && (
                <h3 className="audioPlayerTrackHeader">{nowPlayingTitle}</h3>
            )}
            <audio ref={audioRef} crossOrigin="anonymous" className="audioPlayerAudio" />
            
            {/* Shuffle control */}
            <div className="audioPlayerShuffleContainer">
                <div className="audioPlayerShuffleWrapper" title="Shuffle">
                    <svg 
                        className={`audioPlayerShuffleIcon ${shuffleEnabled ? 'active' : ''}`}
                        onClick={toggleShuffle}
                        width="24" 
                        height="24" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        aria-label="Shuffle"
                        role="img"
                    >
                        <path 
                            fillRule="evenodd" 
                            clipRule="evenodd" 
                            d="M19.4697 4.46967C19.7626 4.17678 20.2374 4.17678 20.5303 4.46967L22.5303 6.46967C22.8232 6.76256 22.8232 7.23744 22.5303 7.53033L20.5303 9.53033C20.2374 9.82322 19.7626 9.82322 19.4697 9.53033C19.1768 9.23744 19.1768 8.76256 19.4697 8.46967L20.1893 7.75H18.3971C17.5531 7.75 16.9733 7.75082 16.517 7.79516C16.077 7.83793 15.8108 7.91703 15.59 8.04205C15.3692 8.16707 15.1644 8.35464 14.9013 8.70996C14.6286 9.07834 14.3295 9.57518 13.8953 10.2989L11.3722 14.5041C10.9613 15.189 10.6254 15.7488 10.3042 16.1826C9.96851 16.636 9.616 16.9989 9.14905 17.2632C8.6821 17.5276 8.18961 17.6432 7.62807 17.6978C7.09086 17.75 6.438 17.75 5.63936 17.75H2C1.58579 17.75 1.25 17.4142 1.25 17C1.25 16.5858 1.58579 16.25 2 16.25H5.60286C6.44685 16.25 7.02675 16.2492 7.48296 16.2048C7.92299 16.1621 8.18919 16.083 8.41 15.9579C8.63081 15.8329 8.8356 15.6454 9.09867 15.29C9.37141 14.9217 9.67046 14.4248 10.1047 13.7011L12.6278 9.49586C13.0387 8.81102 13.3746 8.25119 13.6958 7.8174C14.0315 7.36397 14.384 7.00113 14.851 6.73675C15.3179 6.47237 15.8104 6.35677 16.3719 6.30219C16.9091 6.24998 17.562 6.24999 18.3606 6.25L20.1893 6.25L19.4697 5.53033C19.1768 5.23744 19.1768 4.76256 19.4697 4.46967ZM7.73131 7.79145C7.53519 7.75328 7.31332 7.75 6.66762 7.75H2C1.58579 7.75 1.25 7.41421 1.25 7C1.25 6.58579 1.58579 6.25 2 6.25H6.66762C6.69223 6.25 6.7165 6.24999 6.74045 6.24999C7.28573 6.24986 7.6618 6.24977 8.01789 6.31908C8.81735 6.47469 9.54442 6.88635 10.0892 7.49182C10.3318 7.76151 10.5252 8.08403 10.8057 8.55169C10.818 8.57222 10.8305 8.59303 10.8431 8.61413C11.0562 8.96931 10.9411 9.43001 10.5859 9.64312C10.2307 9.85623 9.76999 9.74106 9.55688 9.38587C9.22467 8.83219 9.10771 8.64362 8.97408 8.49509C8.64722 8.13181 8.21098 7.88481 7.73131 7.79145ZM13.4141 14.3569C13.7693 14.1438 14.23 14.2589 14.4431 14.6141C14.7753 15.1678 14.8923 15.3564 15.0259 15.5049C15.3528 15.8682 15.789 16.1152 16.2687 16.2086C16.4648 16.2467 16.6867 16.25 17.3324 16.25H20.1893L19.4697 15.5303C19.1768 15.23744 19.1768 14.76256 19.4697 14.4697C19.7626 14.1768 20.2374 14.1768 20.5303 14.4697L22.5303 16.4697C22.8232 16.7626 22.8232 17.2374 22.5303 17.5303L20.5303 19.5303C20.2374 19.8232 19.7626 19.8232 19.4697 19.5303C19.1768 19.23744 19.1768 18.76256 19.4697 18.4697L20.1893 17.75H17.3324C17.3078 17.75 17.2835 17.75 17.2595 17.75C16.7143 17.7501 16.3382 17.7502 15.9821 17.6809C15.1826 17.5253 14.4556 17.1137 13.9108 16.5082C13.6682 16.2385 13.4748 15.916 13.1943 15.4483C13.182 15.4278 13.1695 15.407 13.1569 15.3859C12.9438 15.0307 13.0589 14.57 13.4141 14.3569Z" 
                            fill="currentColor"
                        />
                    </svg>
                </div>
            </div>
            
            <div className="audioPlayerControls">
                <button className="audioPlayerButton" onClick={togglePlayPause}>
                    {isPlaying ? 'Pause' : 'Play'}
                </button>
                <button className="audioPlayerButton" onClick={stopPlayback}>
                    Stop
                </button>
                <label className="audioPlayerVolumeLabel">
                    Volume:
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={volume}
                        onChange={handleVolumeChange}
                        className="audioPlayerVolumeSlider"
                    />
                </label>
            </div>

            {/* Progress Bar */}
            <div className="audioPlayerProgressBarContainer">
                <div className="audioPlayerProgressBar" style={{ width: `${progress}%` }}></div>
            </div>

            {/* Track List with Delete Icon */}
            <div className="audioPlayerTrackList">
                {tracks.map((track, index) => {
                    // Calculate whether this track is active - depends on shuffle state
                    let isActive = false;
                    if (currentIndex !== null) {
                        if (shuffleEnabled) {
                            // In shuffle mode, find which original index maps to the current playing index
                            isActive = shuffledIndices[currentIndex] === index;
                        } else {
                            // Regular mode - direct index comparison
                            isActive = index === currentIndex;
                        }
                    }
                    
                    return (
                        <div
                            key={index}
                            className={`audioPlayerTrackItem ${isActive ? 'active' : ''}`}
                        >
                            <span className="audioPlayerTrackTitle" onClick={() => handleTrackClick(index)}>
                                {getTrackTitle(track)}
                            </span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    initiateDelete(index);
                                }}
                                className="audioPlayerDeleteIcon"
                                title="Delete track"
                            >
                                ✕
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Custom Confirmation Modal */}
            {confirmDelete.open && (
                <div className="audioPlayerConfirmModalOverlay">
                    <div className="audioPlayerConfirmModalContent">
                        <p>Are you sure you want to delete this track?</p>
                        <div className="audioPlayerConfirmModalButtons">
                            <button
                                className="audioPlayerCancelButton"
                                onClick={() => setConfirmDelete({ open: false, trackIndex: null })}
                            >
                                Cancel
                            </button>
                            <button
                                className="audioPlayerConfirmButton"
                                onClick={confirmDeleteTrack}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AudioPlayer;
